// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Shunt — the three modes (principle #14). The axis is the SHAPE OF THE YARD, not a difficulty
// dial, because the yard is what decides which routes exist at all:
//
//   Sidings   three stub roads off one headshunt. Everything you want is behind something you
//             do not, and the only lever is which cars you draw out together.
//   Loop      the headshunt has a run-round loop, so the loco can get to the far end of its rake
//             and present it to the roads BACK-TO-FRONT. A whole verb the other yards do not
//             have, and it costs two moves, so "is it worth turning this rake?" is the question
//             the mode is built around.
//   Kickback  one road hangs off ANOTHER ROAD rather than off the headshunt. You cannot reach it
//             without first standing in its parent, so half the yard is behind a door — and
//             every car you leave in the parent road is a car in your own way.
//
// The par bands were MEASURED, not chosen: a full Dijkstra over each yard's reachable positions
// (tests/solve.test.ts re-derives them) shows that openings — loco standing light on the
// headshunt, every car parked — exist only at ODD distances, and how many exist at each. The
// bands below sit where there are hundreds of distinct openings per par value, so a daily seed
// has real variety rather than the same four yards.
//
// The host's chosen mode travels FROZEN inside the round start (rematch roundOpts); a guest
// renders state().hostOpts. An id off the wire is always modeOf()-validated (Object.hasOwn, so a
// wire key can never resolve a prototype member) — unknown falls back to Sidings.

import type { YardSpec } from './rules';

export type ModeId = 'sidings' | 'loop' | 'kickback';

export interface Mode {
  readonly id: ModeId;
  readonly name: string;
  readonly blurb: string;
  readonly spec: YardSpec;
  /** How many cars are in the yard. */
  readonly cars: number;
  /** How many of them the order slip asks for. */
  readonly train: number;
  /** The band of true optimal move counts a generated yard may have (both odd — see above). */
  readonly parMin: number;
  readonly parMax: number;
}

function road(name: string, label: string, parent: number | null, cap: number): YardSpec['tracks'][number] {
  return { name, label, parent, cap };
}

const SIDINGS_SPEC: YardSpec = {
  tracks: [
    road('Headshunt', 'HS', null, 3),
    road('Departure road', 'DEP', 0, 4),
    road('Road 2', '2', 0, 3),
    road('Road 3', '3', 0, 3),
  ],
  departure: 1,
  loop: false,
};

const LOOP_SPEC: YardSpec = {
  tracks: [
    road('Headshunt', 'HS', null, 3),
    road('Departure road', 'DEP', 0, 5),
    road('Road 2', '2', 0, 3),
    road('Road 3', '3', 0, 3),
  ],
  departure: 1,
  loop: true,
};

const KICKBACK_SPEC: YardSpec = {
  tracks: [
    road('Headshunt', 'HS', null, 3),
    road('Road A', 'A', 0, 4),
    road('Departure road', 'DEP', 0, 4),
    road('Kickback', 'K', 1, 3),
  ],
  departure: 2,
  loop: false,
};

export const MODES: Record<ModeId, Mode> = {
  sidings: {
    id: 'sidings',
    name: 'Sidings',
    blurb:
      'Three stub roads off one headshunt, six wagons, a train of four. The plain yard — draw them out, sort them, put them back in order.',
    spec: SIDINGS_SPEC,
    cars: 6,
    train: 4,
    parMin: 7,
    parMax: 11,
  },
  loop: {
    id: 'loop',
    name: 'Run-round',
    blurb:
      'The headshunt has a run-round loop: for two moves the loco can get to the far end of its rake and turn it back-to-front. Seven wagons, a train of five.',
    spec: LOOP_SPEC,
    cars: 7,
    train: 5,
    parMin: 11,
    parMax: 15,
  },
  kickback: {
    id: 'kickback',
    name: 'Kickback',
    blurb:
      'One road hides behind another — you can only reach the kickback by standing in Road A first. Seven wagons, a train of four, and the long way round.',
    spec: KICKBACK_SPEC,
    cars: 7,
    train: 4,
    parMin: 13,
    parMax: 17,
  },
};

export const MODE_IDS: ModeId[] = ['sidings', 'loop', 'kickback'];

export const DEFAULT_MODE: Mode = MODES.sidings;

/** Resolve a (possibly untrusted, off-the-wire) id to a Mode; unknown → Sidings. */
export function modeOf(id: unknown): Mode {
  if (typeof id === 'string' && Object.hasOwn(MODES, id)) {
    return MODES[id as ModeId];
  }
  return DEFAULT_MODE;
}

/** The odd par values a mode may generate, ascending. */
export function parValues(mode: Mode): number[] {
  const out: number[] = [];
  for (let p = mode.parMin; p <= mode.parMax; p += 2) out.push(p);
  return out;
}
